import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import ThreeDModelViewer from "../Components/Model/ThreeDModelViewer";
import AdditionalInfo from "../Components/Product/AdditonInfo/AdditionalInfo";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../Features/Cart/cartSlice";
import toast from "react-hot-toast";
import axios from "axios";


const Product3DDetails = () => {
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState({});
  const [quantity, setQuantity] = useState(1);
  const location = useLocation();
  const defaultColors = {};

  const initialColors = location.state?.objectColors || defaultColors;

  const [objectColors, setObjectColors] = useState(initialColors);
  const [selectedPart, setSelectedPart] = useState(null);
  const cartItem = useSelector((state) => state.cart.items);
  const navigate = useNavigate();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const dispatch = useDispatch();

  const handleColorChange = (color) => {
    if (selectedPart) {
      console.log("Selected Part:", selectedPart);
      console.log("New Color:", color);

      setObjectColors((prevColors) => ({
        ...prevColors,
        [selectedPart]: color || "#ffffff", // default to white if color is invalid
      }));
    } else {
      console.warn(
        "No part selected. Please select a part before applying a color."
      );
    }
  };

  const increment = () => {
    setQuantity(quantity + 1);
  };

  const decrement = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const handleInputChange = (event) => {
    const value = parseInt(event.target.value);
    if (!isNaN(value) && value > 0) {
      setQuantity(value);
    }
  };
  

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_SERVER}/api/products/${id}`)
      .then((res) => setProduct(res.data))
      .catch((err) => {
        console.error("Failed to load 3D product", err);
      });
  }, [id]);

  // ✅ 1. Return early if product hasn't loaded
  if (!product) return <p>Loading 3D product...</p>;

  // ✅ 2. Extra safety: check product.path before using
  if (!product.path) return <p>Invalid product data: missing model path</p>;
 

  const handleAddToCart = (product) => {
    const productDetails = {
      productID: product._id,
      productName: product.title,
      productPrice: product.price,
      frontImg: product.image || product.frontImg,
      image: product.path,
      productReviews: product.reviews,
      size: selectedSize,
      quantity: quantity,
      objectColors,
    };

    const existingCartRaw = localStorage.getItem("cart");
    let existingCart = [];

    try {
      existingCart = JSON.parse(existingCartRaw);
      if (!Array.isArray(existingCart)) existingCart = [];
    } catch (e) {
      existingCart = [];
    }

    const sameProductSameVariantIndex = existingCart.findIndex(
      (item) =>
        item.productID === productDetails.productID &&
        JSON.stringify(item.objectColors) ===
          JSON.stringify(productDetails.objectColors)
    );

    if (sameProductSameVariantIndex !== -1) {
      if (existingCart[sameProductSameVariantIndex].quantity < 20) {
        existingCart[sameProductSameVariantIndex].quantity += 1;
      } else {
        toast.error("Product limit reached", {
          duration: 2000,
          style: { backgroundColor: "#ff4b4b", color: "white" },
          iconTheme: { primary: "#fff", secondary: "#ff4b4b" },
        });
        return;
      }
    } else {
      const sameProductDiffVariantIndex = existingCart.findIndex(
        (item) => item.productID === productDetails.productID
      );

      if (sameProductDiffVariantIndex !== -1) {
        existingCart[sameProductDiffVariantIndex] = {
          ...productDetails,
          quantity: 1,
        };
      } else {
        existingCart.push(productDetails);
      }
    }

    localStorage.setItem("cart", JSON.stringify(existingCart));

    const updatedProduct =
      existingCart.find(
        (item) =>
          item.productID === productDetails.productID &&
          JSON.stringify(item.objectColors) ===
            JSON.stringify(productDetails.objectColors)
      ) || productDetails;

    dispatch(addToCart(updatedProduct)); // ✅ Only one dispatch, and correct item

    toast.success(`Added to cart!`, {
      duration: 2000,
      style: { backgroundColor: "#07bc0c", color: "white" },
      iconTheme: { primary: "#fff", secondary: "#07bc0c" },
    });
  };

  const isInCart = cartItem.some(
    (item) =>
      item.productID === product._id &&
      JSON.stringify(item.objectColors) === JSON.stringify(objectColors)
  );

  return (
    <div className="productSection">
      <div className="productGallery">
        <div className="aspect-ratio-box1">
          <div style={{ width: 500, margin: 40 }}>
            <div
              style={{
                width: "100%",
                height: "450px",
                position: "relative", // Important for Canvas absolute positioning
                borderRadius: "10px",
                overflow: "hidden",
                background: "#eee",
                border: "1px solid #ddd",
              }}
            >
              <Canvas camera={{ position: [0, 0, 3], fov: 35 }}>
                <ambientLight intensity={0.6} />
                <directionalLight position={[2, 3, 3]} />
                <React.Suspense fallback={null}>
                  <ThreeDModelViewer
                    path={product.path}
                    objectColors={objectColors}
                    selectedPart={selectedPart}
                    setSelectedPart={setSelectedPart}
                  />
                </React.Suspense>
                <OrbitControls />
              </Canvas>
            </div>
          </div>
        </div>
        <div className="productDetails" style={{ marginTop: "40px" }}>
          <h1>{product.title}</h1>
          <h4>Price: ${product.price}</h4>
          <h4>Reviews: {product.reviews}</h4>
          <div className="productDescription">
            <h4>Description:</h4>
            <p>{product.description}</p>
          </div>
          <div className="productSizeColor">
            <div className="productSize">
              <h4>Sizes:</h4>
              <div className="sizeBtn">
                {product.size.map((s, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedSize(s)}
                    style={{
                      background: selectedSize === s ? "#333" : "#f9f9f9",
                      color: selectedSize === s ? "#fff" : "#000",
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="productColor">
              <h4>Colors:</h4>
              <div>
                {/* <p>
                  Selected Part: <strong>{selectedPart || "None"}</strong>
                </p> */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                  {product.availableColors.map((color, index) => {
                    const isSelectedColor =
                      selectedPart && objectColors[selectedPart] === color;

                    return (
                      <button
                        key={index}
                        onClick={() => handleColorChange(color)}
                        style={{
                          backgroundColor: color,
                          width: 30,
                          height: 30,
                          borderRadius: "50%",
                          border: isSelectedColor
                            ? "3px solid black"
                            : "1px solid #ccc",
                          outline: "none",
                          cursor: selectedPart ? "pointer" : "not-allowed",
                          opacity: selectedPart ? 1 : 0.5,
                        }}
                        disabled={!selectedPart}
                        title={color}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="productCartQuantity">
              <h4>Quantity:</h4>
              <div className="productQuantity">
                <button onClick={decrement}>-</button>
                <input
                  type="text"
                  value={quantity}
                  onChange={handleInputChange}
                />
                <button onClick={increment}>+</button>
              </div>
              <div className="productCartBtn">
                {isInCart ? (
                  <button onClick={() => navigate("/cart")}>Go to Cart</button>
                ) : (
                  <button onClick={() => handleAddToCart(product)}>
                    Add to Cart
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <AdditionalInfo />
    </div>
  );
};

export default Product3DDetails;
